import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Play,
  CheckCircle,
  XCircle,
  ChevronRight,
  ChevronDown,
  Award,
  Clock,
  FileText,
  Globe,
  HelpCircle,
  Video,
  AlertTriangle,
  ExternalLink,
  Copy,
  Zap,
  Flame,
  Trophy,
  Target,
  ArrowRight,
  BookOpen,
  Route,
  ClipboardList,
  ScrollText,
  Lightbulb,
  Info,
  PhoneCall,
  Scale,
  ClipboardCheck,
  ShieldCheck,
  ArrowRightLeft,
  Star,
  Headphones,
} from 'lucide-react';
import { apiGet, apiPost } from '../../services/api';
import toast from 'react-hot-toast';
import InteractiveVideoPlayer from '../../components/lms/InteractiveVideoPlayer';

import BackButton from '../../components/common/BackButton';
// ── helpers ────────────────────────────────────────────────────────────────────

// Track B: browser TTS listen button (all text-based lessons)
function ListenButton({ text }) {
  const [mode, setMode] = useState(() =>
    (typeof window !== 'undefined' && localStorage.getItem('tts_mode')) || 'browser'
  );
  const [speaking, setSpeaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);
  const stripped = (text || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const enhancedUrl = (import.meta.env.VITE_TTS_ENHANCED_URL || '/api/tts/speak').trim();
  if (!stripped || typeof window === 'undefined') return null;

  const stopAll = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
    setLoading(false);
    setSpeaking(false);
  };

  useEffect(() => () => stopAll(), []);

  const chooseNaturalVoice = () => {
    if (!window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    const enVoices = voices.filter((v) => /^en[-_]/i.test(v.lang || ''));
    const preferredEn = enVoices.find((v) =>
      /(neural|natural|aria|jenny|google|samantha)/i.test(`${v.name} ${v.voiceURI}`));
    return preferredEn || enVoices[0] || voices[0];
  };

  const speakBrowser = () => {
    if (!window.speechSynthesis) return false;
    const utterance = new SpeechSynthesisUtterance(stripped);
    utterance.lang = 'en-US';
    const voice = chooseNaturalVoice();
    if (voice) utterance.voice = voice;
    utterance.rate = 0.96;
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
    return true;
  };

  const speakEnhanced = async () => {
    setLoading(true);
    try {
      const response = await fetch(enhancedUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: stripped }),
      });
      if (!response.ok) throw new Error('Enhanced TTS unavailable');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      audioUrlRef.current = objectUrl;
      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      audio.onended = () => stopAll();
      audio.onerror = () => stopAll();
      await audio.play();
      setSpeaking(true);
    } catch (error) {
      toast.error('Human voice unavailable. Using device voice.');
      setMode('browser');
      if (typeof window !== 'undefined') localStorage.setItem('tts_mode', 'browser');
      speakBrowser();
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    if (speaking) {
      stopAll();
      return;
    }
    if (mode === 'enhanced') {
      await speakEnhanced();
      return;
    }
    speakBrowser();
  };

  const toggleMode = () => {
    const next = mode === 'enhanced' ? 'browser' : 'enhanced';
    setMode(next);
    if (typeof window !== 'undefined') localStorage.setItem('tts_mode', next);
  };

  return (
    <div className="flex items-center gap-1.5">
      <button
        onClick={toggle}
        disabled={loading}
        aria-label={speaking ? 'Stop narration' : `Listen using ${mode === 'enhanced' ? 'human voice' : 'device voice'}`}
        aria-pressed={speaking}
        className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-60 ${
          speaking ? 'bg-violet-100 border-violet-300 text-violet-700' : 'bg-gray-100 border-gray-200 text-gray-500 hover:bg-gray-200'
        }`}
      >
        <Headphones size={12} aria-hidden="true" />
        {loading ? 'Loading...' : speaking ? 'Stop' : 'Listen'}
      </button>
      <button
        onClick={toggleMode}
        aria-label={`Switch to ${mode === 'enhanced' ? 'device' : 'human'} voice`}
        aria-pressed={mode === 'enhanced'}
        className={`text-[11px] px-2 py-1 rounded-full border ${
          mode === 'enhanced'
            ? 'bg-teal-50 border-teal-300 text-teal-700'
            : 'bg-gray-50 border-gray-200 text-gray-500'
        }`}
      >
        {mode === 'enhanced' ? 'Human' : 'Default'}
      </button>
    </div>
  );
}

function getLessonNarrationText(lesson) {
  if (!lesson) return '';
  const chunks = [lesson.title, lesson.description, lesson.content];
  if (lesson.article?.content) chunks.push(lesson.article.content);
  if (lesson.welcome) chunks.push(lesson.welcome.welcomeText, ...(lesson.welcome.objectives || []));
  if (lesson.scenario) {
    chunks.push(lesson.scenario.setting, lesson.scenario.introText);
    (lesson.scenario.steps || []).forEach((s) => {
      chunks.push(s.text);
      (s.choices || []).forEach((c) => chunks.push(c.label, c.outcome));
    });
  }
  if (lesson.summary) {
    (lesson.summary.sections || []).forEach((s) => chunks.push(s.heading, s.body));
    chunks.push(lesson.summary.callout, lesson.summary.commitmentPrompt);
  }
  if (lesson.transition) {
    chunks.push(
      lesson.transition.completedModuleTitle,
      lesson.transition.nextModuleTitle,
      lesson.transition.motivationMessage
    );
    chunks.push(...(lesson.transition.keyLearnings || []));
    if (lesson.transition.warmUpQuestion) {
      chunks.push(lesson.transition.warmUpQuestion.question);
      chunks.push(...(lesson.transition.warmUpQuestion.options || []).map((o) => o.text));
    }
  }
  if (lesson.activity) {
    chunks.push(lesson.activity.instructions, lesson.activity.completionMessage);
    (lesson.activity.tasks || []).forEach((t) => {
      chunks.push(t.prompt);
      (t.items || []).forEach((i) => chunks.push(i.text));
    });
  }
  if (lesson.questions?.length) {
    lesson.questions.forEach((q) => {
      chunks.push(q.question);
      (q.answers || []).forEach((a) => chunks.push(a.text));
    });
  }
  if (lesson.interactiveQuestions?.length) {
    lesson.interactiveQuestions.forEach((q) => {
      chunks.push(q.question);
      (q.options || []).forEach((o) => chunks.push(o.text));
    });
  }
  return chunks.filter(Boolean).join(' ');
}

function resolveCourseVisualCategory(course) {
  const text = [
    course?.category,
    course?.title,
    course?.shortDescription,
    ...(Array.isArray(course?.tags) ? course.tags : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/(onboarding|orientation|new hire|new employee|introduction)/.test(text)) return 'onboarding';
  if (/(quality|qa|qapi|performance improvement|continuous improvement)/.test(text)) return 'quality';
  if (/(operations|operational|workflow|process|sop|procedure)/.test(text)) return 'operations';
  if (/(hr|human resources|people ops|workplace conduct|employee relations)/.test(text)) return 'hr';
  if (/(ethics|code of conduct|integrity|professional responsibility)/.test(text)) return 'ethics';
  if (/(emergency|disaster|evacuation|crisis|incident response|code red|code blue)/.test(text)) return 'emergency';
  if (/(compliance|hipaa|regulatory|legal|rights|policy|audit)/.test(text)) return 'compliance';
  if (/(safety|infection|fire|fall|incident|prevention|abuse|neglect)/.test(text)) return 'safety';
  if (/(clinical|care|cna|nursing|medication|treatment|resident)/.test(text)) return 'clinical';
  if (/(leadership|management|supervisor|communication|culture)/.test(text)) return 'leadership';
  return 'default';
}

function getCourseTheme(category) {
  const themes = {
    compliance: { accent: '#0b7285', accentText: '#155e75', accentSoft: '#e6f6fb', border: '#bae6fd', panelBg: '#f3f9fd', hero: 'linear-gradient(135deg, #0f2027 0%, #164e63 55%, #0e7490 100%)' },
    safety: { accent: '#0f766e', accentText: '#115e59', accentSoft: '#e7f8f6', border: '#99f6e4', panelBg: '#f2fbfa', hero: 'linear-gradient(135deg, #0f2027 0%, #134e4a 55%, #0f766e 100%)' },
    clinical: { accent: '#0369a1', accentText: '#075985', accentSoft: '#eaf4fb', border: '#bae6fd', panelBg: '#f3f9fd', hero: 'linear-gradient(135deg, #082f49 0%, #0c4a6e 55%, #0369a1 100%)' },
    leadership: { accent: '#155e75', accentText: '#164e63', accentSoft: '#e9f4f7', border: '#a5f3fc', panelBg: '#f1f8fb', hero: 'linear-gradient(135deg, #0f172a 0%, #134e4a 55%, #155e75 100%)' },
    onboarding: { accent: '#0f766e', accentText: '#115e59', accentSoft: '#e8f8f5', border: '#99f6e4', panelBg: '#f1faf9', hero: 'linear-gradient(135deg, #0f172a 0%, #134e4a 55%, #0f766e 100%)' },
    quality: { accent: '#0284c7', accentText: '#0369a1', accentSoft: '#e8f5fc', border: '#bae6fd', panelBg: '#f2f9fd', hero: 'linear-gradient(135deg, #082f49 0%, #0c4a6e 55%, #0284c7 100%)' },
    operations: { accent: '#1f6f8b', accentText: '#1e3a5f', accentSoft: '#ebf3f8', border: '#bfdbfe', panelBg: '#f4f8fc', hero: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #1f6f8b 100%)' },
    hr: { accent: '#0f766e', accentText: '#0f766e', accentSoft: '#edf8f2', border: '#a7f3d0', panelBg: '#f3faf7', hero: 'linear-gradient(135deg, #102a43 0%, #14532d 55%, #0f766e 100%)' },
    ethics: { accent: '#0369a1', accentText: '#0c4a6e', accentSoft: '#edf4fb', border: '#bfdbfe', panelBg: '#f2f7fb', hero: 'linear-gradient(135deg, #082f49 0%, #0c4a6e 55%, #0369a1 100%)' },
    emergency: { accent: '#b45309', accentText: '#92400e', accentSoft: '#fff5e6', border: '#fde68a', panelBg: '#fffbf2', hero: 'linear-gradient(135deg, #1f2937 0%, #14532d 50%, #b45309 100%)' },
    default: { accent: '#0891b2', accentText: '#0e7490', accentSoft: '#e8f8fc', border: '#bae6fd', panelBg: '#f4fbfd', hero: 'linear-gradient(135deg, #0f2027 0%, #203a43 55%, #2c5364 100%)' },
  };
  return themes[category] || themes.default;
}

function resolveContentCategory(iconValue, contextText = '') {
  const raw = String(iconValue || '').trim().toLowerCase();
  const context = String(contextText || '').trim().toLowerCase();
  const combined = `${raw} ${context}`.trim();

  const explicit = raw.match(/icon\s*[:=]\s*([a-z0-9_-]+)/i)?.[1]?.toLowerCase();
  if (explicit) {
    if (/(warning|alert|danger|risk)/.test(explicit)) return 'warning';
    if (/(success|check|done|complete|pass)/.test(explicit)) return 'success';
    if (/(tip|idea|hint|lightbulb)/.test(explicit)) return 'tip';
    if (/(goal|objective|target)/.test(explicit)) return 'goal';
    if (/(info|note)/.test(explicit)) return 'info';
    if (/(report|hotline|call|contact)/.test(explicit)) return 'report';
    if (/(law|legal|policy|rights|compliance)/.test(explicit)) return 'legal';
    if (/(document|record|chart|log)/.test(explicit)) return 'document';
    if (/(safety|protect|shield)/.test(explicit)) return 'safety';
    if (/(summary|recap|section|article|text)/.test(explicit)) return 'summary';
  }

  if (/(warning|alert|danger|risk|emergency|911)/i.test(combined)) return 'warning';
  if (/(success|done|complete|pass|check|ok)/i.test(combined)) return 'success';
  if (/(tip|hint|idea|lightbulb)/i.test(combined)) return 'tip';
  if (/(goal|objective|target)/i.test(combined)) return 'goal';
  if (/(report|hotline|call|contact|phone|supervisor|aps|dch|ltco)/i.test(combined)) return 'report';
  if (/(law|legal|policy|rights|compliance|retaliation|mandatory)/i.test(combined)) return 'legal';
  if (/(document|record|chart|log|write|objective documentation)/i.test(combined)) return 'document';
  if (/(protect|protection|safety|safe|resident safety)/i.test(combined)) return 'safety';
  if (/(info|note)/i.test(combined)) return 'info';
  if (/(summary|recap|section|article|text)/i.test(combined)) return 'summary';
  return 'default';
}

function getContentTheme(category) {
  const palette = {
    warning: { card: 'border-amber-200 bg-amber-50/40', chip: 'bg-amber-100', icon: 'text-amber-700', callout: 'bg-amber-50 border-amber-400 text-amber-800' },
    success: { card: 'border-green-200 bg-green-50/40', chip: 'bg-green-100', icon: 'text-green-700', callout: 'bg-green-50 border-green-400 text-green-800' },
    tip: { card: 'border-cyan-200 bg-cyan-50/40', chip: 'bg-cyan-100', icon: 'text-cyan-700', callout: 'bg-cyan-50 border-cyan-400 text-cyan-800' },
    goal: { card: 'border-cyan-200 bg-cyan-50/40', chip: 'bg-cyan-100', icon: 'text-cyan-700', callout: 'bg-cyan-50 border-cyan-400 text-cyan-800' },
    info: { card: 'border-blue-200 bg-blue-50/40', chip: 'bg-blue-100', icon: 'text-blue-700', callout: 'bg-blue-50 border-blue-400 text-blue-800' },
    report: { card: 'border-teal-200 bg-teal-50/40', chip: 'bg-teal-100', icon: 'text-teal-700', callout: 'bg-teal-50 border-teal-400 text-teal-800' },
    legal: { card: 'border-sky-200 bg-sky-50/40', chip: 'bg-sky-100', icon: 'text-sky-700', callout: 'bg-sky-50 border-sky-400 text-sky-800' },
    document: { card: 'border-slate-200 bg-slate-50/60', chip: 'bg-slate-100', icon: 'text-slate-700', callout: 'bg-slate-50 border-slate-400 text-slate-800' },
    safety: { card: 'border-emerald-200 bg-emerald-50/40', chip: 'bg-emerald-100', icon: 'text-emerald-700', callout: 'bg-emerald-50 border-emerald-400 text-emerald-800' },
    summary: { card: 'border-sky-200 bg-sky-50/40', chip: 'bg-sky-100', icon: 'text-sky-700', callout: 'bg-sky-50 border-sky-400 text-sky-800' },
    default: { card: 'border-gray-200 bg-white', chip: 'bg-slate-100', icon: 'text-slate-700', callout: 'bg-blue-50 border-blue-400 text-blue-800' },
  };
  return palette[category] || palette.default;
}

function renderContentIcon(iconValue, opts = {}, contextText = '') {
  const { size = 16, className = '' } = opts;
  const category = resolveContentCategory(iconValue, contextText);
  if (category === 'warning') return <AlertTriangle size={size} className={className} />;
  if (category === 'success') return <CheckCircle size={size} className={className} />;
  if (category === 'tip') return <Lightbulb size={size} className={className} />;
  if (category === 'goal') return <Target size={size} className={className} />;
  if (category === 'info') return <Info size={size} className={className} />;
  if (category === 'report') return <PhoneCall size={size} className={className} />;
  if (category === 'legal') return <Scale size={size} className={className} />;
  if (category === 'document') return <ClipboardCheck size={size} className={className} />;
  if (category === 'safety') return <ShieldCheck size={size} className={className} />;
  if (category === 'summary') return <ScrollText size={size} className={className} />;
  return <FileText size={size} className={className} />;
}

const typeIcon = (type) => {
  switch (type) {
    case 'video':      return <Video size={13} className="text-slate-600" />;
    case 'article':    return <FileText size={13} className="text-slate-600" />;
    case 'pdf':        return <FileText size={13} className="text-slate-600" />;
    case 'quiz':
    case 'interactive': return <HelpCircle size={13} className="text-slate-600" />;
    case 'scorm':      return <Play size={13} className="text-slate-600" />;
    case 'external':   return <Globe size={13} className="text-slate-600" />;
    case 'welcome':    return <BookOpen size={13} className="text-slate-600" />;
    case 'scenario':   return <Route size={13} className="text-slate-600" />;
    case 'summary':    return <ScrollText size={13} className="text-slate-600" />;
    case 'transition': return <ArrowRightLeft size={13} className="text-slate-600" />;
    case 'activity':   return <ClipboardList size={13} className="text-slate-600" />;
    default:           return <FileText size={13} className="text-slate-500" />;
  }
};

// ── SCORM inline player ─────────────────────────────────────────────────────

function SCORMSection({ lesson, enrollmentId, onComplete }) {
  const src = lesson.scorm?.packageUrl
    ? `${lesson.scorm.packageUrl}/${lesson.scorm.launchPath || 'index.html'}`
    : null;

  if (!src) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
        <AlertTriangle size={32} />
        <p>SCORM package URL not configured for this lesson.</p>
        <button
          onClick={() => onComplete({})}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
        >
          Mark Complete
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">
          SCORM {lesson.scorm?.version || '1.2'}
        </span>
        <div className="flex gap-2">
          <a href={src} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-primary-600 hover:underline">
            <ExternalLink size={12} /> Open in new tab
          </a>
        </div>
      </div>
      <iframe
        src={src}
        title={lesson.title}
        className="w-full rounded-lg border border-gray-200"
        style={{ height: 520 }}
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
      />
      <button
        onClick={() => onComplete({})}
        className="w-full py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
      >
        <span className="flex items-center justify-center gap-2">
          <CheckCircle size={16} /> Mark Complete
        </span>
      </button>
    </div>
  );
}

// ── Interactive quiz section ─────────────────────────────────────────────────
function QuizSection({ lesson, enrollmentId, onComplete, onBack, onContinue, completing, isCompleted }) {
  useEffect(() => { toast.remove(); }, []);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);
  const [questionResults, setQuestionResults] = useState({}); // keyed by qKey → { correct, correctOptionIds }
  const [submitting, setSubmitting] = useState(false);

  const useInteractive = (lesson.interactiveQuestions?.length > 0);
  const questions = useInteractive
    ? lesson.interactiveQuestions
    : (lesson.questions || []);

  // stable local key for a question; prefer backend id but fall back to
  // array index so imported/legacy content without identifiers behaves.
  const qKey = (q, idx) => {
    if (q == null) return String(idx ?? '');
    if (q.id || q._id) return String(q.id || q._id);
    return String(idx ?? '');
  };

  const rawRole = String(lesson.assessmentRole || '').trim().toLowerCase();
  const isFinalQuiz = rawRole === 'final_assessment';
  useEffect(() => {
    console.debug('[QuizSection] assessmentRole', lesson._id, lesson.assessmentRole, '=>', rawRole, 'final?', isFinalQuiz);
  }, [lesson._id, lesson.assessmentRole]);

  const handleSelect = (questionKey, optionIdx, isMulti) => {
    if (submitted) return;
    setAnswers((prev) => {
      const cur = new Set(prev[questionKey] || []);
      if (isMulti) { cur.has(optionIdx) ? cur.delete(optionIdx) : cur.add(optionIdx); }
      else { cur.clear(); cur.add(optionIdx); }
      return { ...prev, [questionKey]: cur };
    });
  };

  const handleSubmit = async () => {
    // clear any leftover toast notifications before processing new submission
    toast.remove();
    setSubmitting(true);
    let data;

    // if any question is missing an id then avoid the server entirely; we used to
    // send `undefined` which led to every response being considered correct.
    const needsLocalScoring = questions.some(q => !(q.id || q._id));
    if (needsLocalScoring) {
      const resultsMap = {};
      questions.forEach((q, qi) => {
        const key = qKey(q, qi);
        const opts = (q.options || []).length ? q.options : (q.answers || []);
        const correctIds = new Set(
          opts
            .map((o, oi) =>
              (o.isCorrect || o.correct)
                ? String(o.id ?? o._id ?? oi)
                : null
            )
            .filter(Boolean)
        );
        const sel = new Set(Array.from(answers[key] || []).map(String));
        const isCorrect =
          correctIds.size > 0 &&
          sel.size === correctIds.size &&
          [...sel].every(x => correctIds.has(x));
        resultsMap[key] = { correct: isCorrect, correctOptionIds: correctIds };
      });
      setQuestionResults(resultsMap);
      const total = questions.length;
      const correctCount = Object.values(resultsMap).filter(r => r.correct).length;
      const pct = total ? Math.round((correctCount / total) * 100) : 0;
      const passed = pct >= 70;
      setScore({ pct: isFinalQuiz ? pct : null, passed: isFinalQuiz ? passed : true });
      setSubmitted(true);
      if (isFinalQuiz) {
        if (passed) { toast.success('Quiz passed!'); }
        else { toast.error(`Score: ${pct}%. Need 70% to pass.`); }
      } else {
        toast.success('Quiz complete');
      }
      setSubmitting(false);
      return;
    }

    try {
      // Always send to server — it's the only reliable source of truth for correct answers
      const payload = questions.map((q, qi) => ({
        questionId: qKey(q, qi),
        answerId: [...(answers[qKey(q, qi)] || new Set())].map(String),
      }));

      let data;
      try {
        data = await apiPost(`/lms/lessons/${lesson._id}/answer`, { responses: payload, enrollmentId });
      } catch (err) {
        // if the request fails for any reason just grade client‑side
        console.warn('lesson answer request failed, falling back to local grading', err);
        data = { passed: true, score: 100, questionResults: [] };
      }

      // Index server's per-question results by question id
      const resultsMap = {};
      (data.questionResults || data.results || []).forEach((r) => {
        const id = String(r.questionId || r.id || '');
        if (id) resultsMap[id] = {
          correct: r.correct ?? r.isCorrect ?? null,
          correctOptionIds: new Set((r.correctOptionIds || r.correctAnswerIds || []).map(String)),
        };
      });
      // remap numeric index-based ids to our generated keys
      questions.forEach((q, qi) => {
        const ownKey = qKey(q, qi);
        if (!resultsMap[ownKey]) {
          const numeric = String(qi);
          if (resultsMap[numeric]) resultsMap[ownKey] = resultsMap[numeric];
        }
      });

      // override with local flags when available (takes precedence over server)
      questions.forEach((q, qi) => {
        const key = qKey(q, qi);
        const opts = q.options || [];
        const localCorrectIds = new Set(
          opts
            .map((o, oi) =>
              (o.isCorrect || o.correct) ? String(o.id ?? o._id ?? oi) : null
            )
            .filter(Boolean)
        );
        if (localCorrectIds.size) {
          const existing = resultsMap[key]?.correctOptionIds;
          const differ = existing &&
            (existing.size !== localCorrectIds.size ||
              [...existing].some(x => !localCorrectIds.has(x)));
          if (!existing || differ) {
            resultsMap[key] = { correct: null, correctOptionIds: localCorrectIds };
          }
        }
      });

      // For any question not represented in the server results, try client-side
      // flags as fallback.  This handles the case where only some questions are
      // graded by the backend (e.g. mix of identified and unnamed questions).
      questions.forEach((q, qi) => {
        const key = qKey(q, qi);
        if (!resultsMap[key]) {
          const sel = answers[key] || new Set();
          // Check if options have isCorrect or correct flags
          const hasFlags = (q.options || []).some(o => o.isCorrect != null || o.correct != null);
          if (hasFlags) {
            const correctIdxs = new Set(
              (q.options || []).map((o, oi) => (o.isCorrect || o.correct) ? String(oi) : null).filter(Boolean)
            );
            const selStrs = new Set([...sel].map(String));
            const isCorrect = correctIdxs.size > 0 &&
              selStrs.size === correctIdxs.size && [...selStrs].every(x => correctIdxs.has(x));
            resultsMap[key] = { correct: isCorrect, correctOptionIds: correctIdxs };
          }
        }
      });

      // make sure each entry has a boolean correct value based on answers
      Object.keys(resultsMap).forEach((k) => {
        const r = resultsMap[k];
        if (r.correct == null) {
          const sel = new Set(Array.from(answers[k] || []).map(String));
          if (r.correctOptionIds && r.correctOptionIds.size > 0) {
            r.correct = sel.size === r.correctOptionIds.size && [...sel].every(x => r.correctOptionIds.has(x));
          } else {
            r.correct = false;
          }
        }
      });
      setQuestionResults(resultsMap);
      // recompute overall score from the possibly-augmented resultsMap
      const total = questions.length;
      const correctCount = Object.values(resultsMap).filter(r => r.correct).length;
      const pct = total ? Math.round((correctCount / total) * 100) : 0;
      const passed = pct >= 70;
      setScore({ pct, passed });
      setSubmitted(true);
      if (isFinalQuiz) {
        if (passed) { toast.success('Quiz passed!'); }
        else { toast.error(`Score: ${pct}%. Need 70% to pass.`); }
      } else {
        toast.success('Quiz complete');
      }
    } catch (err) {
      console.error('[QuizSection] submit error', err, 'server data:', data);
      toast.error('Failed to submit quiz');
    }
    finally { setSubmitting(false); }
  };

  if (questions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 gap-3 text-gray-400">
        <HelpCircle size={32} />
        <p>No questions configured for this lesson.</p>
        <button onClick={() => onComplete({})} className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700">Mark Complete</button>
      </div>
    );
  }

  const passed = score ? (score.passed ?? score.pct >= 70) : false;

  // derive score for the result panel display (same logic as worker portal)
  const qTotal = questions.length;
  const qCorrectCount = submitted ? Object.values(questionResults).filter(r => r.correct === true).length : 0;
  const qScorePct = qTotal ? Math.round((qCorrectCount / qTotal) * 100) : 0;
  const quizPassed = submitted && (isFinalQuiz ? (passed) : true);

  const handleRetry = () => {
    toast.remove();
    setSubmitted(false);
    setAnswers({});
    setScore(null);
    setQuestionResults({});
  };

  const handleContinue = async () => {
    await onComplete({ ...(isFinalQuiz && quizPassed ? { score: qScorePct } : {}), skipToast: true });
    onContinue?.();
  };

  return (
    <div className="space-y-6">
      {questions.map((q, qi) => {
        const key = qKey(q, qi);
        const isMulti = (q.questionType || q.type) === 'multiple_select';
        const isOpen = ['reflection', 'long_text', 'short_answer'].includes(q.questionType || q.type);
        const isAttest = (q.questionType || q.type) === 'attestation';
        const sel = answers[key] || new Set();
        const options = useInteractive
          ? (q.options || [])
          : (q.answers || []).map((a) => ({ ...a, _legacyId: a._id }));

        // Server result — null means we don't know correct/incorrect for this question
        const qResult = questionResults[key];
        let qCorrect = submitted ? (qResult?.correct ?? false) : null;

        return (
          <div key={key} className={`rounded-xl border p-5 ${
            submitted && qCorrect !== null
              ? qCorrect ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
              : 'border-gray-200 bg-white'
          }`}>
            <p className="text-xs font-bold tracking-widest uppercase text-primary-500 mb-1">Q{qi + 1} · {(q.questionType || q.type || 'multiple_choice').replace(/_/g, ' ')}</p>
            <p className="font-semibold text-gray-900 leading-snug mb-3">{q.question}</p>
            {isAttest && (
              <label className={`flex items-start gap-3 cursor-pointer rounded-xl border p-3 transition-colors ${sel.has(0) ? 'border-primary-300 bg-primary-50' : 'border-gray-200 bg-gray-50'}`}>
                <input type="checkbox" className="mt-0.5" checked={sel.has(0)} onChange={() => !submitted && handleSelect(key, 0, false)} />
                <span className="text-sm text-gray-700">I confirm this statement</span>
              </label>
            )}
            {isOpen && <textarea className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-400" rows={3} placeholder="Type your response…" disabled={submitted} />}
            {!isAttest && !isOpen && options.length > 0 && (
              <div className="space-y-2">
                {options.map((opt, oi) => {
                  const optId = String(opt.id ?? opt._id ?? oi);
                  const isSelected = sel.has(useInteractive ? oi : (opt._legacyId || oi));
                  // Correct = server correctOptionIds first, then client flags, else unknown
                  const serverCorrectIds = qResult?.correctOptionIds;
                  // hasOptionLevel: we know which specific options are right
                  const hasOptionLevel = serverCorrectIds?.size > 0 || opt.isCorrect != null || opt.correct != null;
                  const isCorrect = submitted && (
                    hasOptionLevel
                      ? (isFinalQuiz
                          ? (serverCorrectIds?.size > 0
                              ? serverCorrectIds.has(optId) || serverCorrectIds.has(String(oi))
                              : !!(opt.isCorrect || opt.correct))
                          : !!(opt.isCorrect || opt.correct) // ignore server for knowledge checks
                        )
                      : (isSelected && qCorrect === true)  // fallback: colour selection by question result
                  );
                  const optExplanation = opt.explanation || opt.feedback || opt.comment || opt.rationale || opt.hint || '';
                  const isWrong = submitted && isSelected && (
                    !isCorrect &&
                    (hasOptionLevel
                      ? true
                      : qCorrect === false || !!optExplanation
                    )
                  );
                  return (
                    <div key={oi}>
                    <button onClick={() => handleSelect(key, useInteractive ? oi : (opt._legacyId || oi), isMulti)} disabled={submitted}
                      className={`w-full text-left text-sm px-4 py-2.5 rounded-xl border font-medium flex items-center gap-2 transition-colors ${
                        isCorrect  ? 'border-green-400 bg-green-50 text-green-800' :
                        isWrong    ? 'border-red-400 bg-red-50 text-red-700' :
                        isSelected ? 'border-primary-400 bg-primary-50 text-primary-800' :
                        'border-gray-200 bg-gray-50 text-gray-700 hover:border-primary-300'
                      } ${submitted ? 'cursor-default' : 'cursor-pointer'}`}>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs shrink-0 font-bold ${
                        isCorrect  ? 'bg-green-500 text-white' :
                        isWrong    ? 'bg-red-400 text-white' :
                        isSelected ? 'bg-primary-500 text-white' :
                        'bg-gray-200 text-gray-500'
                      }`}>
                        {submitted ? (isCorrect ? '✓' : isWrong ? '✗' : String.fromCharCode(65+oi)) : String.fromCharCode(65+oi)}
                      </span>
                      {opt.text}
                      {isCorrect && <CheckCircle size={14} className="ml-auto text-green-600 shrink-0" />}
                      {isWrong && <XCircle size={14} className="ml-auto text-red-400 shrink-0" />}
                    </button>
                    {submitted && optExplanation && (isCorrect || isWrong || isSelected) && (
                      <p className={`mt-1 ml-9 text-xs italic ${isCorrect ? 'text-green-700' : isWrong ? 'text-red-600' : 'text-gray-500'}`}>
                        {optExplanation}
                      </p>
                    )}
                    </div>
                  );
                })}
              </div>
            )}
            {submitted && !isOpen && qCorrect !== null && (
              <p className={`mt-2 text-xs font-medium px-1 ${qCorrect ? 'text-green-600' : 'text-red-500'}`}>
                {qCorrect ? (q.correctFeedback || 'Correct!') : (q.incorrectFeedback || 'Not quite — review and retry.')}
              </p>
            )}
          </div>
        );
      })}

      {!submitted ? (
        <button onClick={handleSubmit} disabled={submitting} className="w-full py-2.5 bg-primary-600 text-white rounded-lg font-semibold text-sm hover:bg-primary-700 disabled:bg-gray-300">
          {submitting ? 'Submitting…' : 'Submit Answers'}
        </button>
      ) : (
        <div className={`rounded-xl p-5 border space-y-1 ${
          isFinalQuiz
            ? (quizPassed ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200')
            : 'bg-slate-50 border-slate-200'
        }`}>
          <p className={`font-semibold text-lg text-center ${
            isFinalQuiz ? (quizPassed ? 'text-green-700' : 'text-red-700') : 'text-slate-700'
          }`}>
            {isFinalQuiz
              ? (quizPassed ? `✓ Passed — ${qScorePct}%` : `✕ Not passed — ${qScorePct}%`)
              : `${qCorrectCount} / ${qTotal} correct`}
          </p>
          <div className="flex items-center justify-center gap-3 pt-2">
            <button
              onClick={handleRetry}
              className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
              Try Again
            </button>
            {(!isFinalQuiz || quizPassed) ? (
              <button
                onClick={handleContinue}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700">
                Continue →
              </button>
            ) : (
              <button
                onClick={handleContinue}
                className="px-5 py-2 text-sm text-gray-400 border border-gray-200 rounded-lg hover:bg-gray-50">
                Continue anyway
              </button>
            )}
          </div>
        </div>
      )}

      {/* Back nav */}
      <div className="flex items-center pt-1">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
          ← Back
        </button>
      </div>
    </div>
  );
}


// ── Gamification Sidebar Panel ─────────────────────────────────────────────────
function GamificationSidebar({ enrollment, course }) {
  // show sidebar if the course has gamification enabled OR learner has earned something
  const enabled = course?.gamification?.enabled;
  const g = enrollment?.gamification || { xp: 0, badges: [], streak: { current: 0 } };
  const xp = g.xp || 0;
  const streak = g.streak?.current || 0;
  const badges = g.badges || [];

  // if nothing at all to display and course doesn't have gamification, render nothing
  if (!enabled && xp === 0 && badges.length === 0 && streak === 0) return null;

  const isEmpty = xp === 0 && badges.length === 0 && streak === 0;

  // Simple level tiers — every 100 XP = 1 level
  const level = Math.floor(xp / 100) + 1;
  const xpIntoLevel = xp % 100;
  const xpNextLevel = 100;

  const xpPerCourse = course?.gamification?.xpPerCourse || 0;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-2.5">
      {/* XP + Level */}
      {xp > 0 ? (
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="flex items-center gap-1 font-semibold text-amber-600">
              <Zap size={11} /> {xp} XP  <span className="text-gray-400 font-normal ml-1">· Lv {level}</span>
            </span>
            <span className="text-gray-400">{xpIntoLevel}/{xpNextLevel}</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all duration-500"
              style={{ width: `${(xpIntoLevel / xpNextLevel) * 100}%` }}
            />
          </div>
        </div>
      ) : isEmpty ? (
        <div className="text-xs text-gray-500">Earn XP by completing lessons to see your progress here.</div>
      ) : null}

      {/* Streak */}
      {streak > 0 && (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-orange-500">
          <Flame size={12} /> {streak}-day streak
          {streak >= 7 && <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full ml-1">🔥 On fire!</span>}
        </div>
      )}

      {/* Badges */}
      {badges.length > 0 && (
        <div>
          <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mb-1.5">
            Badges <span className="text-purple-500">({badges.length})</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {badges.slice(0, 6).map((b, i) => (
              <span
                key={i}
                title={b.name}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 border border-purple-200 rounded-full text-[11px] font-medium text-purple-700"
              >
                {b.icon && <span>{b.icon}</span>}
                {b.name}
              </span>
            ))}
            {badges.length > 6 && (
              <span className="text-[11px] text-gray-400 px-1">+{badges.length - 6} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

export default function CourseViewerPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [accessPolicy, setAccessPolicy] = useState(null);
  const [accessExplanation, setAccessExplanation] = useState(null);
  const [pathBlocked, setPathBlocked] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [expandedModules, setExpandedModules] = useState(new Set());
  const [xpPopup, setXpPopup] = useState(null);
  const [earnedBadges, setEarnedBadges] = useState([]);
  // prevent fetchCourse's auto-select from stealing focus after the initial load
  const hasAutoSelectedRef = useRef(false);
  const visualCategory = useMemo(() => resolveCourseVisualCategory(course), [course]);
  const theme = useMemo(() => getCourseTheme(visualCategory), [visualCategory]);
  const themeVars = useMemo(
    () => ({
      '--cv-accent': theme.accent,
      '--cv-accent-text': theme.accentText,
      '--cv-accent-soft': theme.accentSoft,
      '--cv-border': theme.border,
      '--cv-panel-bg': theme.panelBg,
      '--cv-hero': theme.hero,
    }),
    [theme]
  );

  useEffect(() => { fetchCourse(); }, [courseId]);

  const getProgressPercent = (targetEnrollment) =>
    targetEnrollment?.progress?.percentage ?? targetEnrollment?.progress ?? 0;

  const fetchCourse = async () => {
    try {
      setPathBlocked(null);
      const [courseData, enrollmentsData] = await Promise.all([
        apiGet(`/lms/courses/${courseId}`),
        apiGet('/lms/enrollments?limit=200').catch(() => ({ enrollments: [] })),
      ]);
      const c = courseData.course;
      console.log('fetched course data for viewer', c);
      const matchedEnrollment = (enrollmentsData.enrollments || []).find(
        (entry) => String(entry.courseId?._id || entry.courseId) === String(courseId)
      ) || null;
      setCourse(c);
      setAccessPolicy(courseData.accessPolicy || null);
      setAccessExplanation(courseData.explanation || null);

      // Fetch full enrollment by ID to get gamification, badges, streak populated
      let fullEnrollment = matchedEnrollment;
      if (matchedEnrollment?._id) {
        try {
          const enrollDetail = await apiGet(`/lms/enrollments/${matchedEnrollment._id}`);
          fullEnrollment = enrollDetail.enrollment || enrollDetail || matchedEnrollment;
        } catch {
          // fall back to shallow enrollment
        }
      }
      setEnrollment(fullEnrollment);

      // Expand all modules by default
      setExpandedModules(new Set((c?.modules || []).map((m) => m._id)));

      // Auto-select first incomplete lesson — only on initial load, not on
      // subsequent refreshes triggered by lesson completions.
      if (!hasAutoSelectedRef.current && matchedEnrollment && c?.modules?.length) {
        hasAutoSelectedRef.current = true;
        const prog = matchedEnrollment.lessonProgress || [];
        let found = null;
        outer: for (const mod of c.modules) {
          for (const les of mod.lessons || []) {
            if (!prog.find((p) => String(p.lessonId?._id || p.lessonId) === String(les._id) && p.status === 'completed')) {
              found = les;
              break outer;
            }
          }
        }
        const initialLesson = found || c.modules[0]?.lessons?.[0] || null;
        if (initialLesson) {
          // Use setTimeout so state is settled before fetching
          setTimeout(() => selectLesson(initialLesson), 0);
        }
      } else if (!hasAutoSelectedRef.current) {
        hasAutoSelectedRef.current = true;
        const initialLesson = c?.modules?.[0]?.lessons?.[0] || null;
        if (initialLesson) {
          setTimeout(() => selectLesson(initialLesson), 0);
        }
      }
    } catch (error) {
      const blockedData = error?.response?.data;
      if (
        error?.status === 423 ||
        blockedData?.code === 'PREREQUISITES_UNMET' ||
        blockedData?.code === 'LEARNING_PATH_SEQUENCE_LOCKED'
      ) {
        setPathBlocked({
          message: blockedData?.error || blockedData?.message || 'This course is locked by your learning path.',
          code: blockedData?.code || null,
          learningPathTitle: blockedData?.learningPathTitle || null,
          blockingCourseIds: blockedData?.blockingCourseIds || [],
          unmetPrerequisites: blockedData?.unmetPrerequisites || [],
          plannedPrerequisites: blockedData?.plannedPrerequisites || [],
          explanation: blockedData?.explanation || null,
        });
        setCourse(null);
        setEnrollment(null);
        setAccessPolicy(null);
        setAccessExplanation(blockedData?.explanation || null);
        return;
      }
      toast.error('Failed to load course');
      navigate('/training-management');
    } finally {
      setLoading(false);
    }
  };

  const handleSelfEnroll = async () => {
    if (!course?._id || !accessPolicy?.canSelfEnroll) return;
    setEnrolling(true);
    try {
      await apiPost('/lms/enrollments', { courseId: course._id });
      toast.success('Course started');
      await fetchCourse();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to start course');
    } finally {
      setEnrolling(false);
    }
  };

  const handleCompleteLesson = async (lessonId, extra = {}, options = {}) => {
    if (!enrollment) return;
    setCompleting(true);
    // strip UI-only flags so they aren't sent to the server
    const { skipToast: _skipToast, ...apiExtra } = extra;
    const skipToast = _skipToast || options.skipToast;
    try {
      const data = await apiPost(`/lms/enrollments/${enrollment._id}/progress`, {
        lessonId,
        completed: true,
        ...apiExtra,
      });
      setEnrollment(data.enrollment || enrollment);

      // Floating +XP popup
      if (data.xpAwarded > 0) {
        const popupId = Date.now();
        setXpPopup({ amount: data.xpAwarded, id: popupId });
        setTimeout(() => setXpPopup(p => p?.id === popupId ? null : p), 2200);
      }

      // Collect newly earned badges for session display
      if (data.newBadges?.length) {
        setEarnedBadges(prev => [...prev, ...data.newBadges]);
        data.newBadges.forEach(b =>
          toast.success(`🏅 Badge unlocked: ${b.name}!`, { duration: 4000 })
        );
      }

      // Streak milestone
      const streak = data.enrollment?.gamification?.streak?.current;
      if (streak && streak > 1 && streak % 5 === 0) {
        toast.success(`🔥 ${streak}-day learning streak!`, { duration: 3000 });
      }

      if (!options.skipRefresh) {
        fetchCourse();
      }
      // quizzes show their own pass/fail toast; skip the generic one to avoid doubles
      if (!skipToast) {
        toast.success('Lesson completed!');
      }
    } catch {
      toast.error('Failed to update progress');
    } finally {
      setCompleting(false);
    }
  };

  const isLessonCompleted = (lessonId) => {
    return !!(enrollment?.lessonProgress || []).find(
      (p) => String(p.lessonId?._id || p.lessonId) === String(lessonId) && p.status === 'completed'
    );
  };

  const totalLessons = (course?.modules || []).reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
  const completedLessons = (enrollment?.lessonProgress || []).filter((p) => p.status === 'completed').length;

  const isOverdue =
    enrollment?.dueDate &&
    enrollment.status !== 'completed' &&
    new Date(enrollment.dueDate) < new Date();

  const toggleModule = (id) =>
    setExpandedModules((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  // Fetch full lesson data before displaying — the course load only returns shallow stubs.
  // Without this, nested fields like interactiveQuestions, scenario.steps, article.content,
  // summary.sections, activity.tasks, etc. are all missing for clients and workers.
  const selectLesson = async (lessonStub) => {
    if (!lessonStub?._id) return;
    if (currentLesson?._id === lessonStub._id) return;
    setLoadingLesson(true);
    try {
      const data = await apiGet(`/lms/lessons/${lessonStub._id}`);
      setCurrentLesson(data.lesson || data);
    } catch {
      // Fall back to stub so the user isn't stuck on a blank screen
      setCurrentLesson(lessonStub);
      toast.error('Could not load full lesson content');
    } finally {
      setLoadingLesson(false);
    }
  };

  const orderedLessons = useMemo(
    () => (course?.modules || []).flatMap((module) => module.lessons || []),
    [course]
  );

  const currentLessonIndex = useMemo(
    () => orderedLessons.findIndex((lesson) => String(lesson._id) === String(currentLesson?._id)),
    [orderedLessons, currentLesson?._id]
  );

  const goToPreviousLesson = () => {
    if (currentLessonIndex <= 0) {
      navigate('/learning');
      return;
    }
    selectLesson(orderedLessons[currentLessonIndex - 1]);
  };

  const handleContinueLesson = async () => {
    if (!currentLesson) return;
    const nextLesson = currentLessonIndex >= 0 ? orderedLessons[currentLessonIndex + 1] : null;
    const completed = isLessonCompleted(currentLesson._id);

    if (currentLesson.type === 'video' && !completed) {
      toast.error('Complete this video first to continue.');
      return;
    }

    if (!completed && (currentLesson.type === 'article' || currentLesson.type === 'pdf')) {
      await handleCompleteLesson(currentLesson._id, {}, { skipRefresh: true });
    }

    if (nextLesson) {
      selectLesson(nextLesson);
      return;
    }
    navigate('/learning');
  };

  // Compute module context for the active lesson (used by WelcomeSection)
  const currentModuleContext = useMemo(() => {
    if (!currentLesson || !course?.modules) return { modNum: 0, totalMods: 0, modTitle: '' };
    const modIdx = course.modules.findIndex(m =>
    (m.lessons || []).some(l => String(l._id) === String(currentLesson._id))
    );
    return {
      modNum: modIdx + 1,
      totalMods: course.modules.length,
      modTitle: modIdx >= 0 ? course.modules[modIdx].title : '',
    };
  }, [currentLesson?._id, course?.modules]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (pathBlocked) {
    return (
      <div className="mx-auto max-w-3xl rounded-3xl border border-amber-200 bg-amber-50/60 p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div className="rounded-2xl bg-amber-100 p-3 text-amber-700">
            <AlertTriangle size={24} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-amber-700">Learning Path Lock</p>
            <h1 className="mt-2 text-2xl font-semibold text-gray-900">This course unlocks after earlier steps are completed</h1>
            {pathBlocked.learningPathTitle && (
              <p className="mt-2 text-sm font-medium text-amber-800">
                Path: {pathBlocked.learningPathTitle}
              </p>
            )}
            <p className="mt-3 text-sm text-gray-700">{pathBlocked.message}</p>

            {pathBlocked.code === 'LEARNING_PATH_SEQUENCE_LOCKED' && pathBlocked.blockingCourseIds.length > 0 && (
              <div className="mt-4 rounded-2xl border border-amber-200 bg-white/80 p-4 text-sm text-gray-700">
                This step stays locked until the earlier required course{pathBlocked.blockingCourseIds.length === 1 ? '' : 's'} in the path {pathBlocked.blockingCourseIds.length === 1 ? 'is' : 'are'} complete.
              </div>
            )}

            {pathBlocked.unmetPrerequisites.length > 0 && (
              <div className="mt-6 rounded-2xl border border-amber-200 bg-white/80 p-4">
                <p className="text-sm font-semibold text-gray-900">Complete these prerequisite courses first</p>
                <ul className="mt-3 space-y-2 text-sm text-gray-700">
                  {pathBlocked.unmetPrerequisites.map((item) => (
                    <li key={item._id || item.courseId || item.title} className="flex items-start gap-2">
                      <CheckCircle size={16} className="mt-0.5 text-amber-600" />
                      <span>{item.title || 'Untitled prerequisite course'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pathBlocked.plannedPrerequisites.length > 0 && (
              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-semibold text-gray-900">Planned path steps not live yet</p>
                <ul className="mt-3 space-y-2 text-sm text-gray-600">
                  {pathBlocked.plannedPrerequisites.map((item) => (
                    <li key={item._id || item.courseId || item.title} className="flex items-start gap-2">
                      <Clock size={16} className="mt-0.5 text-slate-500" />
                      <span>{item.title || 'Planned prerequisite course'}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pathBlocked.explanation && (
              <ExplanationPanel explanation={pathBlocked.explanation} className="mt-6" />
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <BackButton fallbackTo={'/training-management'} plainClassName="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-700" />
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!course) return null;

  return (
    <div
      className="flex flex-col lg:flex-row gap-0 -mx-4 -mb-4 sm:-mx-6 sm:-mb-6 lg:-mx-8 lg:-mb-8 min-h-[calc(100vh-4rem)] course-viewer-themed"
      style={themeVars}
    >
      {/* ── Sidebar ─── */}
      <style>{`
        .course-viewer-themed main .bg-white.rounded-xl.border.border-gray-200 {
          background: var(--cv-panel-bg);
          border-color: var(--cv-border);
        }
        .course-viewer-themed main .bg-gradient-to-br {
          background: var(--cv-hero) !important;
        }
        .course-viewer-themed main button[class*='bg-indigo-600'],
        .course-viewer-themed main button[class*='bg-cyan-600'],
        .course-viewer-themed main button[class*='bg-pink-600'],
        .course-viewer-themed main button[class*='bg-violet-600'],
        .course-viewer-themed main button[class*='bg-primary-600'] {
          background-color: var(--cv-accent) !important;
          border-color: var(--cv-accent) !important;
        }
        .course-viewer-themed main button[class*='bg-indigo-600']:hover,
        .course-viewer-themed main button[class*='bg-cyan-600']:hover,
        .course-viewer-themed main button[class*='bg-pink-600']:hover,
        .course-viewer-themed main button[class*='bg-violet-600']:hover,
        .course-viewer-themed main button[class*='bg-primary-600']:hover {
          filter: brightness(0.95);
        }

        /* ── Lesson content CSS variables ── */
        .lesson-content {
          --navy:      #13283e;
          --accent:    #0e7490;
          --accent-lt: #e0f2fe;
          --gold:      #d97706;
          --teal:      #0f766e;
          --green:     #15803d;
          font-size: 15px;
          line-height: 1.65;
          color: #13283e;
        }

        /* Beat label (BEAT 1 · PRIME etc.) */
        .lesson-content .beat-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: .12em;
          text-transform: uppercase;
          color: var(--accent);
          margin-bottom: 14px;
        }

        /* Scenario intro box */
        .lesson-content .scenario-box {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-left: 4px solid var(--accent);
          border-radius: 8px;
          padding: 18px 20px;
        }
        .lesson-content .scenario-box ul {
          list-style: none;
          padding: 0;
          margin: 12px 0 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .lesson-content .scenario-box li {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          font-size: 14px;
        }

        /* Cards */
        .lesson-content .card {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 16px 18px;
          margin-bottom: 12px;
        }
        .lesson-content .card.light {
          background: #f8fafc;
        }

        /* Key point number badge */
        .lesson-content .kp-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--accent);
          color: #fff;
          font-size: 13px;
          font-weight: 700;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .lesson-content .kp-label {
          font-weight: 700;
          font-size: 14px;
          color: var(--navy);
          margin-bottom: 6px;
        }
        .lesson-content .kp-body {
          font-size: 14px;
          color: #374151;
          line-height: 1.6;
          margin: 0;
        }

        /* Infographic grid (Key Takeaways) */
        .lesson-content .infographic-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 12px;
          margin-top: 14px;
        }
        .lesson-content .zone-card {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 14px 16px;
        }
        .lesson-content .zone-card .zicon {
          font-size: 22px;
          margin-bottom: 6px;
        }
        .lesson-content .zone-card h4 {
          font-size: 13px;
          font-weight: 700;
          color: var(--navy);
          margin: 0 0 6px;
        }
        .lesson-content .zone-card p {
          font-size: 13px;
          color: #4b5563;
          line-height: 1.55;
          margin: 0 0 8px;
        }
        .lesson-content .zone-card .tag {
          font-size: 11px;
          font-weight: 600;
          color: var(--teal);
          background: #e0f2fe;
          display: inline-block;
          padding: 2px 8px;
          border-radius: 99px;
        }

        /* Completion box */
        .lesson-content .complete-box {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 28px 24px;
          text-align: center;
        }
        .lesson-content .complete-box .cicon {
          font-size: 36px;
          margin-bottom: 10px;
        }
        .lesson-content .complete-box h2 {
          font-size: 22px;
          font-weight: 700;
          color: var(--navy);
          margin: 0 0 10px;
        }
        .lesson-content .complete-box p {
          font-size: 14px;
          color: #374151;
          line-height: 1.6;
          margin: 0;
        }

        /* General headings inside lesson content */
        .lesson-content h2 {
          font-size: 20px;
          font-weight: 700;
          color: var(--navy);
          margin: 16px 0 10px;
          line-height: 1.3;
        }
        .lesson-content h3 {
          font-size: 16px;
          font-weight: 700;
          color: var(--navy);
          margin: 14px 0 8px;
        }
        .lesson-content p {
          margin: 0 0 10px;
        }
        .lesson-content ul, .lesson-content ol {
          padding-left: 20px;
          margin: 8px 0 12px;
        }
        .lesson-content li {
          margin-bottom: 6px;
          line-height: 1.55;
        }
        .lesson-content strong { font-weight: 700; }
        .lesson-content em { font-style: italic; }
      `}</style>
      <aside className="lg:w-72 xl:w-80 shrink-0 bg-white border-r border-gray-200 flex flex-col lg:h-[calc(100vh-4rem)] lg:sticky lg:top-16">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <BackButton fallbackTo={'/training'} plainClassName="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3" />
          <div className="flex items-start justify-between gap-2">
            <h2 className="font-bold text-gray-900 text-sm line-clamp-2">{course.title}</h2>
            {/* Enrollment status badge — mirrors "published" position in admin preview */}
            {enrollment && (() => {
              const s = enrollment.status;
              const cfg =
                s === 'completed'   ? { label: 'Completed',   cls: 'bg-green-100 text-green-700 border-green-200' } :
                s === 'in_progress' ? { label: 'In Progress', cls: 'bg-blue-100 text-blue-700 border-blue-200' } :
                s === 'overdue'     ? { label: 'Overdue',     cls: 'bg-red-100 text-red-700 border-red-200' } :
                s === 'assigned'    ? { label: 'Assigned',    cls: 'bg-teal-100 text-teal-700 border-teal-200' } :
                null;
              return cfg ? (
                <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>
                  {cfg.label}
                </span>
              ) : null;
            })()}
          </div>
          {isOverdue && (
            <div className="flex items-center gap-1 mt-2 text-xs text-red-600 font-semibold">
              <AlertTriangle size={12} /> Overdue — due {new Date(enrollment.dueDate).toLocaleDateString()}
            </div>
          )}
          {enrollment?.dueDate && !isOverdue && enrollment.status !== 'completed' && (
            <p className="text-xs text-amber-600 mt-1">
              <Clock size={11} className="inline mr-1" />
              Due {new Date(enrollment.dueDate).toLocaleDateString()}
            </p>
          )}
          {enrollment && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>{completedLessons}/{totalLessons} lessons</span>
                <span className="font-medium">{getProgressPercent(enrollment)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-primary-600 rounded-full transition-all" style={{ width: `${getProgressPercent(enrollment)}%` }} />
              </div>
              {/* Gamification — show if enabled on course OR enrollment has accumulated XP/badges */}
              {(course?.gamification?.enabled ||
                enrollment?.gamification?.xp > 0 ||
                (enrollment?.gamification?.badges || []).length > 0 ||
                enrollment?.gamification?.streak?.current > 0) && (
                <GamificationSidebar enrollment={enrollment} course={course} />
              )}
            </div>
          )}
        </div>

        {/* Module progress rail + lesson tree */}
        <div className="flex-1 overflow-y-auto">
          {(course.modules || []).map((mod, modIdx) => {
            const open = expandedModules.has(mod._id);
            const lessons = mod.lessons || [];
            const modDone  = lessons.filter(l => isLessonCompleted(l._id)).length;
            return (
              <div key={mod._id}>
                <button
                  onClick={() => toggleModule(mod._id)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 text-left border-b border-gray-100"
                >
                  <div className="flex-1 min-w-0 pr-2">
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide block truncate">
                      {mod.title}
                    </span>
                    {/* ── Module progress rail ─ */}
                    {lessons.length > 0 && (
                      <div className="flex items-center gap-0.5 mt-1.5">
                        {lessons.map((les) => {
                          const done   = isLessonCompleted(les._id);
                          const active = currentLesson?._id === les._id;
                          return (
                            <div
                              key={les._id}
                              role="button"
                              tabIndex={0}
                              title={les.title}
                              aria-label={`Open lesson: ${les.title}`}
                              onClick={(e) => { e.stopPropagation(); selectLesson(les); }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  selectLesson(les);
                                }
                              }}
                              className={`h-1.5 rounded-full transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400 ${
                                active ? 'w-5 bg-primary-500' :
                                done   ? 'w-2 bg-green-500'   :
                                         'w-2 bg-gray-300 hover:bg-gray-400'
                              }`}
                            />
                          );
                        })}
                        <span className="ml-1.5 text-xs text-gray-400">{modDone}/{lessons.length}</span>
                      </div>
                    )}
                  </div>
                  {open
                    ? <ChevronDown  size={14} className="text-gray-400 shrink-0" />
                    : <ChevronRight size={14} className="text-gray-400 shrink-0" />
                  }
                </button>

                {open && lessons.map((les) => {
                  const done   = isLessonCompleted(les._id);
                  const active = currentLesson?._id === les._id;
                  return (
                    <button
                      key={les._id}
                      onClick={() => selectLesson(les)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm border-b border-gray-50 transition-colors ${
                        active ? 'bg-primary-50 border-l-4 border-l-primary-500' : 'hover:bg-gray-50'
                      }`}
                    >
                      <span className="shrink-0">
                        {done
                          ? <CheckCircle size={15} className="text-green-500" />
                          : typeIcon(les.type)}
                      </span>
                      <span className={`flex-1 text-sm leading-tight ${
                        done ? 'text-gray-400' : 'text-gray-900'
                      }`}>
                        {les.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Certificate button */}
        {enrollment?.status === 'completed' && enrollment?.certificate?.certificateId && (
          <div className="p-4 border-t border-gray-200">
            <Link
              to={`/certificates/${enrollment.certificate.certificateId}`}
              className="flex items-center justify-center gap-2 w-full py-2 bg-amber-500 text-white rounded-lg text-sm font-semibold hover:bg-amber-600"
            >
              <Award size={16} /> View Certificate
            </Link>
          </div>
        )}
      </aside>

      {/* ── Main content ─── */}
      <main className="flex-1 p-4 sm:p-6 overflow-y-auto relative">
        {/* Floating +XP popup */}
        {xpPopup && (
          <div
            key={xpPopup.id}
            className="pointer-events-none fixed bottom-24 right-8 z-50 flex items-center gap-1.5 px-4 py-2 rounded-full bg-amber-400 text-white font-bold text-sm shadow-lg"
            style={{ animation: 'xpFloat 2.2s ease-out forwards' }}
          >
            <Zap size={14} /> +{xpPopup.amount} XP
          </div>
        )}
        <style>{`
          @keyframes xpFloat {
            0%   { opacity: 0; transform: translateY(0px) scale(0.8); }
            15%  { opacity: 1; transform: translateY(-8px) scale(1.05); }
            70%  { opacity: 1; transform: translateY(-24px) scale(1); }
            100% { opacity: 0; transform: translateY(-48px) scale(0.95); }
          }
        `}</style>

        {/* Earned badges this session */}
        {earnedBadges.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {earnedBadges.map((b, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-xs font-semibold text-amber-700">
                <Trophy size={12} /> {b.icon && <span>{b.icon}</span>} {b.name}
              </span>
            ))}
          </div>
        )}
        {accessExplanation && (
          <ExplanationPanel explanation={accessExplanation} className="mb-4" />
        )}
        {!enrollment ? (
          <div className="max-w-lg mx-auto text-center py-16">
            <GraduationCapIcon />
            <h2 className="text-xl font-bold text-gray-900 mt-4 mb-2">{course.title}</h2>
            <p className="text-gray-500 mb-2">{course.description}</p>
            {accessPolicy?.canSelfEnroll ? (
              <div className="space-y-4">
                <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  This course is available for self-start under your organization&apos;s training access.
                </p>
                <button
                  type="button"
                  onClick={handleSelfEnroll}
                  disabled={enrolling}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-400"
                >
                  <Play size={16} />
                  {enrolling ? 'Starting…' : 'Start Course'}
                </button>
              </div>
            ) : (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                This course is not assigned to you yet. Please contact your admin or staffing coordinator.
              </p>
            )}
          </div>
        ) : enrollment.status === 'completed' ? (
          <CompletionBanner enrollment={enrollment} course={course} earnedBadges={earnedBadges} />
        ) : loadingLesson ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600" />
          </div>
        ) : currentLesson ? (
          <LessonContent
            key={currentLesson._id}
            lesson={currentLesson}
            enrollment={enrollment}
            theme={theme}
            moduleContext={currentModuleContext}
            isCompleted={isLessonCompleted(currentLesson._id)}
            onComplete={(extra) => handleCompleteLesson(currentLesson._id, extra)}
            onBack={goToPreviousLesson}
            onContinue={handleContinueLesson}
            completing={completing}
          />
        ) : (
          <div className="text-center py-16 text-gray-400">Select a lesson to begin.</div>
        )}
      </main>
    </div>
  );
}

// ── sub-components ─────────────────────────────────────────────────────────────

function GraduationCapIcon() {
  return (
    <div className="w-20 h-20 mx-auto bg-primary-100 rounded-full flex items-center justify-center">
      <Award size={40} className="text-primary-600" />
    </div>
  );
}

function ExplanationPanel({ explanation, className = '' }) {
  const sections = [
    { title: 'Why visible', items: explanation?.visibilityReasons || [] },
    { title: 'Why available', items: explanation?.assignabilityReasons || [] },
    { title: 'What is blocking this', items: explanation?.blockingReasons || [] },
    { title: 'Helpful context', items: explanation?.supportingReasons || [] },
  ].filter((section) => section.items.length > 0);

  if (sections.length === 0) return null;

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm ${className}`}>
      <div className="flex items-center gap-2">
        <Info size={16} className="text-slate-500" />
        <h3 className="text-sm font-semibold text-slate-900">Why you are seeing this course</h3>
      </div>
      <div className="mt-3 space-y-3">
        {sections.map((section) => (
          <div key={section.title}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{section.title}</p>
            <ul className="mt-1 space-y-1 text-sm text-slate-700">
              {section.items.map((item) => (
                <li key={item.code} className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
                  <span>{item.label}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function getCompletionState(course, enrollment) {
  const issuanceRule =
    course?.certificateIssuanceRule ||
    (course?.certificateScoringMode === 'overall' ? 'overall_pass' : 'final_assessment_pass');
  const gradeCounted = !!course?.certificateEnabled && ['final_assessment_pass', 'final_assessment_and_attestation', 'overall_pass'].includes(issuanceRule);
  const requiredScore = Number.isFinite(Number(course?.passingScore)) ? Number(course.passingScore) : 70;
  const scoreSource = issuanceRule === 'overall_pass'
    ? Number(enrollment?.scores?.overall)
    : Number(enrollment?.scores?.finalAssessment?.score);
  const hasScore = Number.isFinite(scoreSource);

  if (gradeCounted && hasScore && scoreSource >= requiredScore) {
    return {
      kind: 'passed',
      title: "Congratulations, you've passed!",
      subtitle: issuanceRule === 'overall_pass'
        ? 'Your course score met the required passing score for'
        : 'Your final assessment met the required passing score for',
      showConfetti: true,
      scoreText: `${issuanceRule === 'overall_pass' ? 'Course Score' : 'Final Score'}: ${scoreSource}% (Required: ${requiredScore}%)`,
    };
  }
  if (gradeCounted && hasScore && scoreSource < requiredScore) {
    return {
      kind: 'completed_not_passed',
      title: "You've reached the end of your course",
      subtitle: issuanceRule === 'overall_pass'
        ? 'Course score is below passing score. Please review and retake'
        : 'Final assessment is below passing score. Please review and retake',
      showConfetti: false,
      scoreText: `${issuanceRule === 'overall_pass' ? 'Course Score' : 'Final Score'}: ${scoreSource}% (Required: ${requiredScore}%)`,
    };
  }
  return {
    kind: 'completed',
    title: "You've reached the end of your course",
    subtitle: 'Course content is complete for',
    showConfetti: false,
    scoreText: hasScore ? `Final Score: ${overallScore}%` : '',
  };
}

function ConfettiBurst({ active }) {
  const pieces = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 500}ms`,
        duration: `${1200 + Math.random() * 900}ms`,
        color: ['#14b8a6', '#0ea5e9', '#22c55e', '#f59e0b'][i % 4],
      })),
    []
  );
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (!active || reducedMotion) return null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{`
        @keyframes lms-confetti-fall {
          0% { transform: translateY(-30px) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(360px) rotate(540deg); opacity: 0; }
        }
      `}</style>
      {pieces.map((p) => (
        <span
          key={p.id}
          className="absolute top-0 w-2 h-3 rounded-sm"
          style={{
            left: p.left,
            backgroundColor: p.color,
            animationName: 'lms-confetti-fall',
            animationTimingFunction: 'ease-out',
            animationFillMode: 'forwards',
            animationDuration: p.duration,
            animationDelay: p.delay,
          }}
        />
      ))}
    </div>
  );
}

function CompletionBanner({ enrollment, course, earnedBadges }) {
  const completion = getCompletionState(course, enrollment);
  const iconTone =
    completion.kind === 'passed'
      ? 'bg-green-100 text-green-600'
      : completion.kind === 'completed_not_passed'
        ? 'bg-amber-100 text-amber-600'
        : 'bg-cyan-100 text-cyan-700';

  const totalXp = enrollment?.gamification?.xp || 0;
  const streak = enrollment?.gamification?.streak?.current || 0;
  const badges = enrollment?.gamification?.badges || [];
  const gamifEnabled = course?.gamification?.enabled || totalXp > 0 || badges.length > 0 || streak > 0;

  return (
    <div className="relative max-w-xl mx-auto text-center py-16 space-y-4">
      <ConfettiBurst active={completion.showConfetti || (gamifEnabled && completion.kind !== 'completed_not_passed')} />
      <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center ${iconTone}`}>
        <CheckCircle size={48} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900">{completion.title}</h2>
      <p className="text-gray-600">{completion.subtitle} <strong>{course.title}</strong>.</p>
      {completion.scoreText && (
        <p className="text-lg font-semibold text-primary-700">{completion.scoreText}</p>
      )}

      {/* Gamification summary */}
      {gamifEnabled && (totalXp > 0 || streak > 0 || badges.length > 0) && (
        <div className="mt-4 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-5 text-left space-y-3">
          <p className="text-xs font-bold tracking-widest uppercase text-amber-700 text-center mb-3">Your Achievements</p>
          <div className="flex justify-center gap-6">
            {totalXp > 0 && (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-1">
                  <Zap size={22} className="text-amber-500" />
                </div>
                <p className="text-lg font-bold text-amber-700">{totalXp}</p>
                <p className="text-xs text-amber-600">Total XP</p>
              </div>
            )}
            {streak > 0 && (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-orange-100 flex items-center justify-center mb-1">
                  <Flame size={22} className="text-orange-500" />
                </div>
                <p className="text-lg font-bold text-orange-600">{streak}</p>
                <p className="text-xs text-orange-500">Day Streak</p>
              </div>
            )}
            {badges.length > 0 && (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-purple-100 flex items-center justify-center mb-1">
                  <Trophy size={22} className="text-purple-500" />
                </div>
                <p className="text-lg font-bold text-purple-600">{badges.length}</p>
                <p className="text-xs text-purple-500">Badge{badges.length !== 1 ? 's' : ''}</p>
              </div>
            )}
          </div>
          {badges.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center pt-1">
              {badges.map((b, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-amber-200 rounded-full text-xs font-medium text-amber-700">
                  {b.icon && <span>{b.icon}</span>} {b.name}
                </span>
              ))}
            </div>
          )}
          {earnedBadges.length > 0 && (
            <p className="text-xs text-center text-amber-600 font-medium">
              🎉 {earnedBadges.length} badge{earnedBadges.length !== 1 ? 's' : ''} earned this session!
            </p>
          )}
        </div>
      )}

      {enrollment.certificate?.certificateId && (
        <Link
          to={`/certificates/${enrollment.certificate.certificateId}`}
          className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600"
        >
          <Award size={20} /> View Your Certificate
        </Link>
      )}
      <div className="pt-4">
        <BackButton fallbackTo={"/training"} plainClassName="inline-flex items-center gap-2 text-primary-600 hover:underline text-sm" />
      </div>
    </div>
  );
}

function LessonContent({ lesson, enrollment, isCompleted, onComplete, onBack, onContinue, completing, theme, moduleContext }) {
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Lesson header — editorial style */}
      <div className="mb-5">
        <span
          className="inline-block text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3"
          style={{ background: theme.accentSoft, color: theme.accentText }}
        >
          {lesson.type === 'article' ? 'reading' : lesson.type}
        </span>
        <h2
          className="text-2xl lg:text-4xl leading-tight tracking-tight"
          style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", serif', color: '#13283e' }}
        >
          {lesson.title}
        </h2>
        <div className="flex items-center gap-3 mt-1.5 text-sm text-[#7a8ca1]">
          {(lesson.duration || lesson.estimatedTime) > 0 && (
            <span className="flex items-center gap-1">
              <Clock size={13} /> {lesson.type === 'article' ? '7-8 min read' : `${lesson.duration || lesson.estimatedTime} minutes`}
            </span>
          )}
          {lesson.description && <span>{lesson.description}</span>}
        </div>
        {lesson.type !== 'video' && (
          <div className="mt-3">
            <ListenButton text={getLessonNarrationText(lesson)} />
          </div>
        )}
        {isCompleted && (
          <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium mt-2">
            <CheckCircle size={12} /> Completed
          </span>
        )}
      </div>

      {/* Content by type */}
      {lesson.type === 'video' && (
        <VideoSection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} onBack={onBack} onContinue={onContinue} completing={completing} />
      )}
      {lesson.type === 'article' && (
        <ArticleSection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} onBack={onBack} onContinue={onContinue} completing={completing} />
      )}
      {lesson.type === 'pdf' && (
        <PDFSection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} onBack={onBack} onContinue={onContinue} completing={completing} />
      )}
      {(lesson.type === 'quiz' || lesson.type === 'interactive') && (
        <QuizSection lesson={lesson} enrollmentId={enrollment._id} onComplete={onComplete} onBack={onBack} onContinue={onContinue} completing={completing} isCompleted={isCompleted} />
      )}
      {lesson.type === 'scorm' && (
        <SCORMSection lesson={lesson} enrollmentId={enrollment._id} onComplete={onComplete} />
      )}
      {lesson.type === 'external' && (
        <ExternalSection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} completing={completing} />
      )}
      {lesson.type === 'welcome' && (
        <WelcomeSection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} completing={completing} theme={theme} moduleContext={moduleContext} />
      )}
      {lesson.type === 'scenario' && (
        <ScenarioSection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} onBack={onBack} onContinue={onContinue} completing={completing} theme={theme} />
      )}
      {lesson.type === 'summary' && (
        <SummarySection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} completing={completing} theme={theme} />
      )}
      {lesson.type === 'transition' && (
        <TransitionSection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} completing={completing} theme={theme} />
      )}
      {lesson.type === 'activity' && (
        <ActivitySection lesson={lesson} isCompleted={isCompleted} onComplete={onComplete} completing={completing} theme={theme} />
      )}
    </div>
  );
}

function VideoSection({ lesson, isCompleted, onComplete, onBack, onContinue, completing }) {
  const [canComplete, setCanComplete] = useState(isCompleted);

  const videoUrl = lesson.videoUrl ||
    (lesson.video?.provider === 'vimeo' && lesson.video?.url) ||
    (lesson.video?.provider === 'youtube' && lesson.video?.url) ||
    lesson.video?.url;

  if (!videoUrl) {
    return (
      <div className="bg-gray-900 rounded-xl aspect-video flex items-center justify-center text-gray-500">
        No video configured
      </div>
    );
  }

  const isEmbed = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') || videoUrl.includes('vimeo.com');

  return (
    <div className="space-y-4">
      {isEmbed ? (
        <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
          <iframe
            src={videoUrl.replace('watch?v=', 'embed/')}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={lesson.title}
            onLoad={() => setCanComplete(true)}
          />
        </div>
      ) : (
        <InteractiveVideoPlayer
          videoUrl={videoUrl}
          captionsUrl={lesson.video?.captions?.[0]?.url || null}
          transcript={lesson.transcript || null}
          questions={lesson.interactiveQuestions || []}
          onProgress={({ percentage }) => { if (percentage >= 90) setCanComplete(true); }}
          onComplete={() => setCanComplete(true)}
        />
      )}
      {lesson.audioUrl && (
        <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-1">
          <p className="text-xs font-medium text-violet-700 flex items-center gap-1">
            <Headphones size={12} aria-hidden="true" /> Audio alternative - listen instead of watching
          </p>
          <audio src={lesson.audioUrl} controls className="w-full" onEnded={() => setCanComplete(true)} />
        </div>
      )}
      {isEmbed && lesson.transcript && (
        <details className="border border-gray-200 rounded-lg bg-gray-50">
          <summary className="cursor-pointer select-none px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
            <FileText size={14} aria-hidden="true" /> View transcript
          </summary>
          <div className="px-4 pb-4 pt-2 max-h-64 overflow-y-auto">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{lesson.transcript}</p>
          </div>
        </details>
      )}
      {!isCompleted && (
        <button
          onClick={() => onComplete({})}
          disabled={!canComplete || completing}
          className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {completing ? 'Saving...' : canComplete ? 'Mark Complete' : 'Watch 90% to unlock'}
        </button>
      )}
      <div className="flex items-center justify-between pt-1">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
          ← Back
        </button>
        <button
          onClick={onContinue}
          disabled={!isCompleted}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

function ArticleSection({ lesson, isCompleted, onComplete, onBack, onContinue, completing }) {
  return (
    <div className="space-y-4">
      {lesson.article?.contentUrl ? (
        <iframe
          src={lesson.article.contentUrl}
          style={{ width: '100%', minHeight: '75vh', border: 'none', display: 'block' }}
          title={lesson.title}
          sandbox="allow-scripts allow-same-origin allow-forms"
          className="rounded-xl border border-gray-200 bg-white"
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          {lesson.article?.content || lesson.content
            ? <div className="lesson-content lesson-content--editorial" dangerouslySetInnerHTML={{ __html: lesson.article?.content || lesson.content }} />
            : <p className="text-gray-400 italic">No content available for this lesson.</p>
          }
        </div>
      )}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
          ← Back
        </button>
        <button
          onClick={onContinue}
          disabled={completing}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {completing ? 'Saving...' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}

function PDFSection({ lesson, isCompleted, onComplete, onBack, onContinue, completing }) {
  const url = lesson.pdf?.url || lesson.pdfUrl || lesson.content;
  return (
    <div className="space-y-4">
      {url ? (
        <iframe src={url} title={lesson.title} className="w-full border border-gray-200 rounded-xl" style={{ height: 600 }} />
      ) : (
        <div className="flex items-center justify-center h-40 text-gray-400 border border-gray-200 rounded-xl">
          No PDF configured
        </div>
      )}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
          ← Back
        </button>
        <button
          onClick={onContinue}
          disabled={completing}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {completing ? 'Saving...' : 'Continue →'}
        </button>
      </div>
    </div>
  );
}

function ExternalSection({ lesson, isCompleted, onComplete, completing }) {
  const url = lesson.externalUrl || lesson.content;
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
        <Globe size={40} className="mx-auto text-teal-500 mb-4" />
        <p className="text-gray-600 mb-4">{lesson.description || 'This lesson opens an external resource.'}</p>
        {url && (
          <a href={url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700">
            <ExternalLink size={16} /> Open Resource
          </a>
        )}
      </div>
      {!isCompleted && (
        <button onClick={() => onComplete({})} disabled={completing} className="w-full py-2.5 bg-green-600 text-white rounded-lg font-semibold text-sm hover:bg-green-700 disabled:bg-gray-300">
          {completing ? 'Saving...' : 'Mark Complete'}
        </button>
      )}
    </div>
  );
}

// ─ Welcome lesson ────────────────────────────────────────────────────────────
function WelcomeSection({ lesson, isCompleted, onComplete, completing, theme, moduleContext }) {
  const { welcome } = lesson;
  const welcomeText = welcome?.welcomeText || lesson.welcomeText || '';
  // welcomeText may be plain-text noise duplicating the objectives — only show if it looks like real HTML
  const welcomeHtml = welcomeText.trim().startsWith('<') ? welcomeText : '';
  const objectives = Array.isArray(welcome?.objectives) && welcome.objectives.length
    ? welcome.objectives
    : Array.isArray(lesson.objectives) ? lesson.objectives : [];
  const estimatedTime = welcome?.estimatedTime || lesson.estimatedTime || lesson.duration || 0;
  const bannerImage = welcome?.bannerImage || lesson.bannerImage || null;

  const { modNum = 0, totalMods = 0, modTitle = '' } = moduleContext || {};

  // If welcome lesson has an external iframe override
  const customContentUrl = lesson.article?.contentUrl || null;
  const customInlineHtml = lesson.article?.content || null;

  if (customContentUrl) {
    return (
      <div className="space-y-4">
        <iframe src={customContentUrl} style={{ width: '100%', minHeight: '75vh', border: 'none', display: 'block' }}
          title={lesson.title} sandbox="allow-scripts allow-same-origin allow-forms"
          className="rounded-2xl border border-gray-200 bg-white" />
        {!isCompleted && (
          <button onClick={() => onComplete({})} disabled={completing}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-50"
            style={{ background: theme.accent }}>
            {completing ? 'Saving...' : "Let's Begin →"}
          </button>
        )}
      </div>
    );
  }

  if (customInlineHtml && customInlineHtml !== welcomeText) {
    return (
      <div className="space-y-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="lesson-content lesson-content--editorial" dangerouslySetInnerHTML={{ __html: customInlineHtml }} />
        </div>
        {!isCompleted && (
          <button onClick={() => onComplete({})} disabled={completing}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-50"
            style={{ background: theme.accent }}>
            {completing ? 'Saving...' : "Let's Begin →"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {bannerImage && (
        <img src={bannerImage} alt="" className="w-full rounded-2xl object-cover max-h-56" />
      )}

      {/* Hero card — matches admin preview exactly */}
      <div className="rounded-2xl p-6 text-white overflow-hidden relative" style={{ background: theme.hero }}>
        <div className="relative z-10">
          {/* MODULE X OF Y badge */}
          {modNum > 0 && (
            <p className="text-xs font-bold tracking-widest uppercase mb-3" style={{ color: 'rgba(125,211,252,0.9)' }}>
              Module {modNum} of {totalMods}
            </p>
          )}
          {/* Module title as heading */}
          <h3 className="text-xl font-bold leading-snug mb-3">
            {modTitle || lesson.title}
          </h3>
          {/* Welcome body text rendered as HTML */}
          {welcomeHtml ? (
            <div
              className="text-sm leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.9)' }}
              dangerouslySetInnerHTML={{ __html: welcomeHtml }}
            />
          ) : (
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
              No introduction content is available for this lesson yet.
            </p>
          )}
        </div>
      </div>

      {/* What You Will Learn — objectives */}
      {objectives.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h4 className="font-semibold text-gray-900 mb-4">What You Will Learn</h4>
          <ol className="space-y-3">
            {objectives.map((obj, i) => {
              const cleanObj = String(obj).replace(/^\d{2}/, '').trim();
              return (
              <li key={i} className="flex items-start gap-3">
                <span
                  className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ background: theme.accent }}
                >
                  {i + 1}
                </span>
                <span className="text-sm text-gray-700 pt-0.5">{cleanObj}</span>
              </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Estimated time */}
      {estimatedTime > 0 && (
        <div className="border-l-4 rounded-r-xl px-4 py-3 text-sm"
          style={{ borderColor: theme.accent, background: theme.accentSoft, color: theme.accentText }}>
          ⏱ Estimated time to complete this module: <strong>{estimatedTime} minutes</strong>
        </div>
      )}

      {!isCompleted && (
        <button onClick={() => onComplete({})} disabled={completing}
          className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-colors disabled:opacity-50"
          style={{ background: theme.accent }}>
          {completing ? 'Saving…' : 'Start Module →'}
        </button>
      )}
    </div>
  );
}

// ─ Guided Scenario ───────────────────────────────────────────────────────
function ScenarioSection({ lesson, isCompleted, onComplete, onBack, onContinue, completing, theme }) {
  const { scenario } = lesson;
  const steps = scenario?.steps || [];
  const [currentStepId, setCurrentStepId] = useState(steps[0]?.id || null);
  const [choiceOutcome, setChoiceOutcome] = useState(null);
  const [history, setHistory] = useState([]);
  const [done, setDone] = useState(false);

  const currentStep = steps.find(s => s.id === currentStepId);
  const currentIdx = steps.indexOf(currentStep);

  const handleChoice = (choice) => {
    setChoiceOutcome({ text: choice.outcome, correct: choice.correct, nextStepId: choice.nextStepId });
    setHistory(prev => [...prev, { step: currentStep, choice }]);
  };

  const handleNext = () => {
    if (!choiceOutcome) return;
    const { nextStepId } = choiceOutcome;
    setChoiceOutcome(null);
    if (!nextStepId || !steps.find(s => s.id === nextStepId)) {
      setDone(true);
    } else {
      setCurrentStepId(nextStepId);
    }
  };

  if (lesson.article?.contentUrl) {
    return (
      <div className="space-y-4">
        <iframe
          src={lesson.article.contentUrl}
          style={{ width: '100%', minHeight: '75vh', border: 'none', display: 'block' }}
          title={lesson.title}
          sandbox="allow-scripts allow-same-origin allow-forms"
          className="rounded-xl border border-gray-200"
        />
        {!isCompleted && (
          <button
            onClick={() => onComplete({})}
            disabled={completing}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
            style={{ background: theme.accent }}
          >
            {completing ? 'Saving...' : 'Mark Complete'}
          </button>
        )}
      </div>
    );
  }

  if (!steps.length) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 border border-gray-200 rounded-xl">
        No scenario steps configured.
      </div>
    );
  }

  if (done) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl p-8 text-white text-center space-y-4" style={{ background: theme.hero }}>
          <CheckCircle size={40} className="mx-auto" style={{ color: 'rgba(255,255,255,0.8)' }} />
          <p className="text-xl font-bold">Scenario Complete!</p>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>You've worked through all the situations.</p>
        </div>
        {history.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-3">Your Journey</p>
            <div className="space-y-2">
              {history.map((h, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${
                    h.choice.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {String.fromCharCode(65 + (h.step?.choices || []).indexOf(h.choice))}
                  </span>
                  <span className="text-gray-600 leading-snug">{h.choice.label || h.choice.outcome}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {!isCompleted && (
          <button
            onClick={() => onComplete({})}
            disabled={completing}
            className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
            style={{ background: theme.accent }}
          >
            {completing ? 'Saving...' : 'Mark Complete'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Progress — matches preview: label + dots inline */}
      <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
        <span className="text-sm font-bold tracking-widest uppercase text-gray-500">
          Situation {currentIdx + 1} of {steps.length}
        </span>
        <div className="flex gap-1.5">
          {steps.map((s, i) => (
            <span key={s.id} className={`rounded-full transition-all ${
              i === currentIdx ? 'w-3 h-3' : 'w-2 h-2'
            }`} style={{
              background: i < currentIdx ? theme.accentSoft : i === currentIdx ? theme.accent : '#e5e7eb'
            }} />
          ))}
        </div>
      </div>

      {/* introText only shown if different from setting */}
      {scenario?.introText && scenario.introText !== scenario?.setting && currentIdx === 0 && !choiceOutcome && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          {scenario.introText}
        </div>
      )}

      {/* Setting — matches preview: dark gradient, icon, label */}
      {(scenario?.setting || currentStep?.setting) && (
        <div className="rounded-2xl p-5 flex gap-3" style={{ background: theme.hero }}>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0"
            style={{ background: 'rgba(255,255,255,0.15)' }}>🏥</div>
          <div>
            <p className="text-sm font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.7)' }}>Setting:</p>
            <p className="text-sm text-white leading-relaxed">{scenario?.setting || currentStep.setting}</p>
          </div>
        </div>
      )}

      {currentStep && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
          {/* SITUATION N label + decision point heading */}
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-1">
              Situation {currentIdx + 1}
            </p>
            {currentStep.text && (
              <p className="font-semibold text-gray-900 leading-snug">{currentStep.text}</p>
            )}
          </div>

          {currentStep.image && (
            <img src={currentStep.image} alt="" className="w-full rounded-xl object-cover max-h-48" />
          )}

          {/* Choices or outcome */}
          {choiceOutcome ? (
            <div className={`rounded-xl p-4 space-y-3 ${
              choiceOutcome.correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <p className={`text-sm font-medium ${choiceOutcome.correct ? 'text-green-800' : 'text-red-800'}`}>
                {choiceOutcome.correct ? '✅' : '❌'} {choiceOutcome.text}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {(currentStep.choices || []).map((c, ci) => (
                <button
                  key={c.id}
                  onClick={() => handleChoice(c)}
                  className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 hover:border-amber-400 hover:bg-amber-50 transition-colors flex items-start gap-3"
                >
                  <span className="shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-bold flex items-center justify-center mt-0.5">
                    {String.fromCharCode(65 + ci)}
                  </span>
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Next Situation button — shown after a choice is made, matching preview */}
      {choiceOutcome && (
        <button
          onClick={handleNext}
          className="w-full py-2.5 rounded-xl text-white font-semibold text-sm transition-colors"
          style={{ background: theme.accent }}
        >
          Next Situation →
        </button>
      )}

      {/* Back nav */}
      <div className="flex items-center justify-between pt-1">
        <button onClick={onBack} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-50">
          ← Back
        </button>
      </div>
    </div>
  );
}

// ─ Infographic Summary ───────────────────────────────────────────────────
function SummarySection({ lesson, isCompleted, onComplete, completing, theme }) {
  const { summary } = lesson;
  const [commitment, setCommitment] = useState('');
  const [confidence, setConfidence] = useState(0);
  const [clarityNote, setClarityNote] = useState('');
  const calloutCategory = resolveContentCategory(summary?.calloutIcon || summary?.calloutType || '', summary?.callout || '');
  const calloutTheme = getContentTheme(calloutCategory);

  const handleComplete = () => {
    onComplete({
      commitmentResponse: commitment || undefined,
      confidenceRating:   confidence || undefined,
      clarityNote:        clarityNote || undefined,
    });
  };

  return (
    <div className="space-y-4">
      {summary?.imageUrl && (
        <img src={summary.imageUrl} alt="" className="w-full rounded-xl object-cover max-h-60" />
      )}

      {(summary?.sections || []).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {summary.sections.map((sec, i) => {
            const secCategory = resolveContentCategory(sec.icon || '', `${sec.heading || ''} ${sec.body || ''}`);
            const secTheme = getContentTheme(secCategory);
            return (
              <div key={i} className={`rounded-2xl border p-4 hover:shadow-sm transition-shadow ${secTheme.card}`}>
                {(sec.icon || sec.heading || sec.body) && (
                  <span className={`w-8 h-8 rounded-lg mb-2 inline-flex items-center justify-center ${secTheme.chip}`}>
                    {renderContentIcon(sec.icon, { size: 16, className: secTheme.icon }, `${sec.heading || ''} ${sec.body || ''}`)}
                  </span>
                )}
                {sec.heading && <p className="font-semibold text-gray-900 text-sm mb-1.5">{sec.heading}</p>}
                {sec.body && <p className="text-sm text-gray-600 leading-relaxed">{sec.body}</p>}
              </div>
            );
          })}
        </div>
      )}

      {summary?.callout && (
        <div className={`px-4 py-3 rounded-xl border-l-4 text-sm leading-relaxed flex gap-3 ${calloutTheme.callout}`}>
          <span className="shrink-0 w-5 h-5 mt-0.5 inline-flex items-center justify-center">
            {renderContentIcon(
              summary.calloutIcon || summary.calloutType || 'info',
              { size: 16, className: calloutTheme.icon },
              summary.callout
            )}
          </span>
          <span>{summary.callout}</span>
        </div>
      )}

      {/* RECALL Phase 5 — LOCK IT IN: commitment prompt */}
      {!isCompleted && summary?.commitmentPrompt && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-2">
          <p className="text-sm font-semibold text-emerald-800">📌 Commit to Action</p>
          <p className="text-sm text-emerald-700">{summary.commitmentPrompt}</p>
          <textarea
            value={commitment}
            onChange={e => setCommitment(e.target.value)}
            placeholder="I will try this by..."
            className="w-full border border-emerald-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent resize-none bg-white"
            rows={2}
          />
        </div>
      )}

      {/* RECALL Phase 7 — REFLECT: confidence + clarity */}
      {!isCompleted && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">How confident do you feel using this skill right now?</p>
            <div className="flex gap-2 items-center">
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  onClick={() => setConfidence(n)}
                  className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold border-2 transition-all ${
                    confidence === n
                      ? 'bg-amber-400 border-amber-500 text-white'
                      : 'border-gray-300 text-gray-500 hover:border-amber-400'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">What’s still unclear? <span className="text-xs text-gray-400">(optional)</span></p>
            <textarea
              value={clarityNote}
              onChange={e => setClarityNote(e.target.value)}
              placeholder="Anything from this module you’d like reviewed..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              rows={2}
            />
          </div>
        </div>
      )}

      {!isCompleted && (
        <button onClick={handleComplete} disabled={completing} className="w-full py-2.5 bg-pink-600 text-white rounded-lg font-semibold text-sm hover:bg-pink-700 disabled:bg-gray-300">
          {completing ? 'Saving...' : 'Mark Complete'}
        </button>
      )}
    </div>
  );
}

// ─ Module Transition ───────────────────────────────────────────────────────
function TransitionSection({ lesson, isCompleted, onComplete, completing }) {
  const { transition } = lesson;
  const wq = transition?.warmUpQuestion;
  const [wqAnswer, setWqAnswer] = useState(null);
  const [wqSubmitted, setWqSubmitted] = useState(false);

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl border border-cyan-100 p-6 space-y-5">

        {/* Completed module */}
        {transition?.completedModuleTitle && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle size={18} className="text-green-600" />
              <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Just Completed</span>
            </div>
            <p className="text-lg font-bold text-gray-900">{transition.completedModuleTitle}</p>
          </div>
        )}

        {/* Key learnings */}
        {transition?.keyLearnings?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">What We Covered</p>
            <ul className="space-y-1.5">
              {transition.keyLearnings.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle size={14} className="text-teal-500 shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Motivation message */}
        {transition?.motivationMessage && (
          <div className="bg-white/60 rounded-lg px-4 py-3 text-sm text-gray-700 italic border border-cyan-100">
            “{transition.motivationMessage}”
          </div>
        )}

        {/* Next module */}
        {transition?.nextModuleTitle && (
          <div className="flex items-center gap-3 pt-2 border-t border-cyan-100">
            <ArrowRight size={18} className="text-cyan-600" />
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Up Next</p>
              <p className="font-bold text-cyan-900">{transition.nextModuleTitle}</p>
            </div>
          </div>
        )}
      </div>

      {!isCompleted && (
        <button onClick={() => onComplete({})} disabled={completing} className="w-full py-2.5 bg-cyan-600 text-white rounded-lg font-semibold text-sm hover:bg-cyan-700 disabled:bg-gray-300">
          {completing ? 'Saving…' : 'Continue to Next Module →'}
        </button>
      )}
    </div>
  );
}

// ─ Hands-on Activity ───────────────────────────────────────────────────────
function ActivitySection({ lesson, isCompleted, onComplete, completing }) {
  const { activity } = lesson;
  const tasks = activity?.tasks || [];
  const [responses, setResponses] = useState({});

  const requiredDone = tasks
    .filter(t => t.required !== false)
    .every(t => responses[t.id] && String(responses[t.id]).trim().length > 0);

  const setResponse = (id, val) => setResponses(prev => ({ ...prev, [id]: val }));

  return (
    <div className="space-y-4">
      {activity?.instructions && (
        <div
          className="prose prose-sm max-w-none text-gray-700"
          dangerouslySetInnerHTML={{ __html: activity.instructions }}
        />
      )}

      {tasks.map((task, ti) => (
        <div key={task.id || ti} className="bg-violet-50 border border-violet-200 rounded-xl p-4 space-y-2">
          <p className="text-sm font-semibold text-violet-900">
            Task {ti + 1}{task.required !== false && <span className="text-red-500 ml-0.5">*</span>}
          </p>
          <p className="text-sm text-gray-700">{task.prompt}</p>

          {(task.type === 'text_input' || !task.type) && (
            <textarea
              value={responses[task.id] || ''}
              onChange={e => setResponse(task.id, e.target.value)}
              placeholder={task.placeholder || 'Type your response here...'}
              className="w-full border border-violet-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-400 focus:border-transparent resize-none bg-white"
              rows={3}
              disabled={isCompleted}
            />
          )}

          {task.type === 'checklist' && (
            <div className="space-y-1.5">
              {(task.items || []).map(item => (
                <label key={item.id} className="flex items-center gap-2.5 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-violet-600"
                    checked={!!(responses[task.id]?.includes(item.id))}
                    disabled={isCompleted}
                    onChange={e => {
                      const curr = responses[task.id] || [];
                      setResponse(task.id, e.target.checked
                        ? [...curr, item.id]
                        : curr.filter(i => i !== item.id));
                    }}
                  />
                  {item.text}
                </label>
              ))}
            </div>
          )}
        </div>
      ))}

      {activity?.completionMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
          {activity.completionMessage}
        </div>
      )}

      {!isCompleted && (
        <button
          onClick={() => onComplete({ activityResponses: responses })}
          disabled={completing || !requiredDone}
          className="w-full py-2.5 bg-violet-600 text-white rounded-lg font-semibold text-sm hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          {completing ? 'Saving…' : requiredDone ? '✓ Submit Activity' : 'Complete all required tasks to continue'}
        </button>
      )}
    </div>
  );
}
